import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import SearchBar from './components/SearchBar';
import BookCard from './components/BookCard';
import AdvancedSearch from './components/AdvancedSearch';
import BookReaderPreview from './components/BookReaderPreview';
import SpecialCollections from './components/SpecialCollections';
import IntegratedResources from './components/IntegratedResources';
import MultilingualSelector from './components/MultilingualSelector';
import UserFeedback from './components/UserFeedback';
import CopyrightProtection from './components/CopyrightProtection';
import RecentlyAddedBooks from './components/RecentlyAddedBooks';
import AccessibilityButton from './components/AccessibilityButton';
import AccessibilityPanel from './components/AccessibilityPanel';
import SpeakableText from './components/SpeakableText';
import { getPlaceholderImage } from './config/constants';
import { useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';

// Date fictive pentru demo până vom conecta la backend
const mockBooks = [
  {
    id: 1,
    title: "Micul Prinț",
    author: "Antoine de Saint-Exupéry",
    description: "O poveste despre prietenie, iubire și responsabilitate, spusă prin ochii unui pilot care se prăbușește în deșert și întâlnește un tânăr prinț de pe o planetă îndepărtată.",
    coverImage: "https://m.media-amazon.com/images/I/71OZY035QKL._AC_UF1000,1000_QL80_.jpg",
    category: "Ficțiune",
    publicationYear: 1943,
    language: "Română",
    pages: 96
  },
  {
    id: 2,
    title: "1984",
    author: "George Orwell",
    description: "Un roman distopic care prezintă un viitor sumbru în care guvernul totalitar, condus de misteriosul Big Brother, controlează fiecare aspect al vieții cetățenilor.",
    coverImage: "https://m.media-amazon.com/images/I/71kxa1-0mfL._AC_UF1000,1000_QL80_.jpg",
    category: "Distopie",
    publicationYear: 1949,
    language: "Română",
    pages: 328
  },
  {
    id: 3,
    title: "Mândrie și prejudecată",
    author: "Jane Austen",
    description: "O satiră a moravurilor sociale și o poveste de dragoste între Elizabeth Bennet și orgoliosul domn Darcy, într-o societate în care căsătoria era adesea o necesitate economică.",
    coverImage: "https://m.media-amazon.com/images/I/71Q1tPupKjL._AC_UF1000,1000_QL80_.jpg",
    category: "Clasic",
    publicationYear: 1813,
    language: "Română",
    pages: 432
  },
  {
    id: 4,
    title: "Amintiri din copilărie",
    author: "Ion Creangă",
    description: "Povestiri autobiografice care descriu copilăria autorului într-un sat din Moldova, capturând frumusețea vieții rurale și a tradițiilor românești.",
    coverImage: "https://cdn.dc5.ro/img-prod/1018849893-0.jpeg",
    category: "Memorialistică",
    publicationYear: 1892,
    language: "Română",
    pages: 180
  },
  {
    id: 5,
    title: "Hobbitul",
    author: "J.R.R. Tolkien",
    description: "Aventura lui Bilbo Baggins, un hobbit care pornește într-o călătorie neașteptată pentru a recupera comoara păzită de dragonul Smaug.",
    coverImage: "https://m.media-amazon.com/images/I/710+HcoP38L._AC_UF1000,1000_QL80_.jpg",
    category: "Fantasy",
    publicationYear: 1937,
    language: "Română",
    pages: 310
  },
  {
    id: 6,
    title: "Sapiens: Scurtă istorie a omenirii",
    author: "Yuval Noah Harari",
    description: "O explorare a istoriei umane, de la apariția speciei Homo sapiens până în prezent, analizând impactul revoluțiilor cognitive, agricole și științifice.",
    coverImage: "https://m.media-amazon.com/images/I/71N3-2sYxQL._AC_UF1000,1000_QL80_.jpg",
    category: "Non-Ficțiune",
    publicationYear: 2011,
    language: "Română",
    pages: 464
  }
];

// Date pentru colecții speciale
const mockSpecialCollections = [
  {
    id: "rare-manuscripts",
    title: "Manuscrise Rare",
    description: "O colecție de manuscrise rare din secolele XVII-XIX, digitizate pentru conservare și acces public.",
    coverImage: "https://www.printmag.com/wp-content/uploads/2020/06/b7da91_c0155f458f9a4c03a11597f7181c6dba_mv2.jpg",
    itemCount: 42,
    category: "Istorie",
    highlightItems: [
      {
        id: "ms-001",
        title: "Cronica Țării Românești (fragment)",
        description: "Manuscris din secolul al XVII-lea, care descrie evenimente istorice din Țara Românească.",
        image: "https://www.romania-insider.com/sites/default/files/styles/article_large_image/public/2020-01/neacsu_document_biblioteca_academiei_romane_credit_bar.jpg",
        year: "1650",
        type: "Manuscris"
      },
      {
        id: "ms-002",
        title: "Psaltirea Scheiană",
        description: "Una dintre primele traduceri în limba română a Psaltirii, un document important pentru istoria limbii române.",
        year: "1482",
        type: "Carte religioasă"
      }
    ]
  },
  {
    id: "historical-maps",
    title: "Hărți Istorice",
    description: "Colecție de hărți istorice din diferite perioade, oferind o perspectivă asupra evoluției geografice a României și Europei.",
    coverImage: "https://4.bp.blogspot.com/-y8iYKYjpg8k/WM4Xr4XLYqI/AAAAAAAAAGM/Cw1ABUaJmLUYv4UWJONGcPEBK2GIRwSegCEw/s1600/Descriptio%2BMoldaviae%2B-%2Bharta.jpg",
    itemCount: 78,
    category: "Cartografie",
    highlightItems: [
      {
        id: "map-001",
        title: "Descriptio Moldaviae",
        description: "Hartă detaliată a Moldovei realizată de Dimitrie Cantemir, cu descrieri geografice și etnografice.",
        image: "https://4.bp.blogspot.com/-y8iYKYjpg8k/WM4Xr4XLYqI/AAAAAAAAAGM/Cw1ABUaJmLUYv4UWJONGcPEBK2GIRwSegCEw/s1600/Descriptio%2BMoldaviae%2B-%2Bharta.jpg",
        year: "1716",
        type: "Hartă"
      }
    ]
  },
  {
    id: "old-newspapers",
    title: "Ziare și Periodice Vechi",
    description: "Arhivă de ziare și periodice din secolele XIX-XX, oferind informații despre evenimente istorice și culturale.",
    coverImage: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Newspaper_romania_gazeta_transilvaniei_brasov_3_15_mai_1891.jpg/1200px-Newspaper_romania_gazeta_transilvaniei_brasov_3_15_mai_1891.jpg",
    itemCount: 156,
    category: "Presă",
    highlightItems: [
      {
        id: "news-001",
        title: "Gazeta de Transilvania (1840)",
        description: "Primul număr al Gazetei de Transilvania, unul dintre cele mai importante ziare românești din secolul XIX.",
        image: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Newspaper_romania_gazeta_transilvaniei_brasov_3_15_mai_1891.jpg/1200px-Newspaper_romania_gazeta_transilvaniei_brasov_3_15_mai_1891.jpg",
        year: "1840",
        type: "Ziar"
      }
    ]
  }
];

// Date pentru resurse externe integrate
const mockExternalResources = [
  {
    id: "ext-001",
    title: "Teoria relativității - Explicată simplu",
    description: "Un ghid accesibil despre teoria relativității a lui Einstein, cu explicații și ilustrații pentru publicul general.",
    author: "Stephen Hawking",
    thumbnailUrl: "https://m.media-amazon.com/images/I/61kRkfsIMUL._AC_UF1000,1000_QL80_.jpg",
    sourceLibrary: "Biblioteca Națională de Științe",
    sourceLibraryLogo: getPlaceholderImage('BNS', 50, 50),
    sourceUrl: "#",
    type: "book" as const,
    availability: "available" as const
  },
  {
    id: "ext-002",
    title: "Impactul inteligenței artificiale asupra societății moderne",
    description: "Articol academic care analizează efectele dezvoltării tehnologiilor AI asupra diferitelor aspecte ale vieții sociale.",
    author: "Institutul de Cercetări Avansate",
    thumbnailUrl: getPlaceholderImage('AI', 150, 150),
    sourceLibrary: "Arhiva Digitală Academică",
    sourceLibraryLogo: getPlaceholderImage('ADA', 50, 50),
    sourceUrl: "#",
    type: "article" as const,
    availability: "restricted" as const
  },
  {
    id: "ext-003",
    title: "Biodiversitatea în Delta Dunării",
    description: "Teză de doctorat care studiază ecosistemele din Delta Dunării și impactul schimbărilor climatice asupra acestora.",
    author: "Dr. Maria Popescu",
    thumbnailUrl: getPlaceholderImage('Delta', 150, 150),
    sourceLibrary: "Biblioteca Universității din București",
    sourceLibraryLogo: getPlaceholderImage('UB', 50, 50),
    sourceUrl: "#",
    type: "thesis" as const,
    availability: "available" as const
  },
  {
    id: "ext-004",
    title: "Interviu cu laureatul premiului Nobel pentru literatură",
    description: "Înregistrare audio a unui interviu exclusiv cu laureatul premiului Nobel pentru literatură din 2020.",
    author: "Arhiva Radio Cultural",
    thumbnailUrl: getPlaceholderImage('Audio', 150, 150),
    sourceLibrary: "Mediateca Națională",
    sourceLibraryLogo: getPlaceholderImage('MN', 50, 50),
    sourceUrl: "#",
    type: "audio" as const,
    availability: "subscription" as const
  }
];

// Date pentru politici de copyright
const mockCopyrightPolicies = [
  {
    id: "cp-001",
    title: "Utilizare educațională",
    description: "Politici pentru utilizarea materialelor în scop educațional și academic.",
    details: "Materialele din biblioteca digitală pot fi utilizate pentru scopuri educaționale și de cercetare, cu respectarea următoarelor condiții: <ul><li>Citarea corectă a sursei</li><li>Utilizarea doar în context educațional</li><li>Fără distribuire comercială</li></ul>"
  },
  {
    id: "cp-002",
    title: "Materiale în domeniul public",
    description: "Informații despre operele din domeniul public și modul lor de utilizare.",
    details: "Operele din domeniul public pot fi utilizate fără restricții, dar vă rugăm să verificați cu atenție statutul fiecărei lucrări. În general, operele intră în domeniul public după 70 de ani de la moartea autorului."
  },
  {
    id: "cp-003",
    title: "Utilizare personală",
    description: "Politici pentru utilizarea materialelor în scop personal.",
    details: "Utilizatorii pot descărca și stoca materiale pentru uz personal, fără drept de redistribuire sau utilizare comercială."
  }
];

// Date pentru recenzii
const mockBookRatings = {
  rating: 4.2,
  userCount: 128,
  breakdown: {
    5: 67,
    4: 35,
    3: 18,
    2: 5,
    1: 3
  }
};

const mockBookReviews = [
  {
    id: "rev-001",
    userName: "Alexandra M.",
    userAvatar: "https://randomuser.me/api/portraits/women/44.jpg",
    rating: 5,
    date: "2023-10-15",
    content: "O carte fascinantă care m-a ținut captivată de la început până la sfârșit. Recomand cu căldură tuturor iubitorilor de literatură clasică.",
    likes: 12,
    hasLiked: false,
    replies: [
      {
        id: "rep-001",
        userName: "Biblioteca Digitală",
        userAvatar: getPlaceholderImage('BD', 40, 40),
        date: "2023-10-16",
        content: "Mulțumim pentru recenzia pozitivă! Suntem bucuroși că v-a plăcut cartea.",
        isAuthor: true
      }
    ]
  },
  {
    id: "rev-002",
    userName: "Mihai P.",
    userAvatar: "https://randomuser.me/api/portraits/men/32.jpg",
    rating: 4,
    date: "2023-09-28",
    content: "O lucrare excelentă, deși pe alocuri am simțit că ritmul narațiunii încetinește prea mult. În ansamblu, o experiență de lectură plăcută.",
    likes: 5,
    hasLiked: true
  }
];

const AppContent: React.FC = () => {
  const { t } = useLanguage();
  const [books, setBooks] = useState(mockBooks);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [selectedBook, setSelectedBook] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<'books' | 'collections' | 'external'>('books');
  const [searchFilters, setSearchFilters] = useState<any>({});
  const { user, token } = useAuth();

  // Activăm scurtăturile de tastatură pentru accesibilitate
  useKeyboardShortcuts();

  // Categorii pentru showcase cu traduceri dinamice
  const categories = [
    { id: 1, name: t('categories.fiction'), icon: "📚", count: 245 },
    { id: 2, name: t('categories.nonFiction'), icon: "🧠", count: 186 },
    { id: 3, name: t('categories.science'), icon: "🔬", count: 120 },
    { id: 4, name: t('categories.academic'), icon: "🎓", count: 98 },
    { id: 5, name: t('categories.biography'), icon: "👤", count: 74 },
    { id: 6, name: t('categories.poetry'), icon: "🖋️", count: 53 }
  ];

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // În viitor, aici vom face request către backend
    if (!query.trim()) {
      setBooks(mockBooks);
      return;
    }
    
    const filteredBooks = mockBooks.filter(book => 
      book.title.toLowerCase().includes(query.toLowerCase()) || 
      book.author.toLowerCase().includes(query.toLowerCase()) || 
      book.category?.toLowerCase().includes(query.toLowerCase())
    );
    
    setBooks(filteredBooks);
  };

  const handleAdvancedSearch = (filters: any) => {
    setSearchFilters(filters);
    // Aici ar trebui să facem un request către backend cu filtrele
    // Pentru demo, filtrăm local
    let filteredResults = [...mockBooks];
    
    if (filters.title) {
      filteredResults = filteredResults.filter(book => 
        book.title.toLowerCase().includes(filters.title.toLowerCase())
      );
    }
    
    if (filters.author) {
      filteredResults = filteredResults.filter(book => 
        book.author.toLowerCase().includes(filters.author.toLowerCase())
      );
    }
    
    if (filters.genre) {
      filteredResults = filteredResults.filter(book => 
        book.category?.toLowerCase() === filters.genre.toLowerCase()
      );
    }
    
    if (filters.year?.min) {
      filteredResults = filteredResults.filter(book => 
        book.publicationYear && book.publicationYear >= filters.year.min
      );
    }
    
    if (filters.year?.max) {
      filteredResults = filteredResults.filter(book => 
        book.publicationYear && book.publicationYear <= filters.year.max
      );
    }
    
    if (filters.language) {
      filteredResults = filteredResults.filter(book => 
        book.language?.toLowerCase() === filters.language.toLowerCase()
      );
    }
    
    setBooks(filteredResults);
    setShowAdvancedSearch(false);
  };

  const handleBookClick = (bookId: number) => {
    const book = mockBooks.find(b => b.id === bookId);
    if (book) {
      setSelectedBook({
        ...book,
        content: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
        totalPages: 320,
        currentPage: 1
      });
    }
  };

  const handleCloseBookPreview = () => {
    setSelectedBook(null);
  };

  const handleCollectionClick = (collectionId: string) => {
    console.log(`Navigating to collection: ${collectionId}`);
    // În implementarea reală, aici am naviga la pagina colecției
  };

  const handleCollectionItemClick = (collectionId: string, itemId: string) => {
    console.log(`Viewing item ${itemId} from collection ${collectionId}`);
    // În implementarea reală, aici am deschide un viewer pentru elementul respectiv
  };

  const handleExternalResourceClick = (resource: any) => {
    console.log(`Opening external resource: ${resource.title}`);
    // În implementarea reală, aici am deschide resursa într-o fereastră nouă sau ar redirecționa
    window.open(resource.sourceUrl, '_blank');
  };

  const handleAddReview = (bookId: number, rating: number, content: string) => {
    console.log(`Adding review for book ${bookId}: ${rating} stars, "${content}"`);
    // În implementarea reală, aici am face un POST către backend
  };

  const handleLikeReview = (reviewId: string) => {
    console.log(`Liking review ${reviewId}`);
    // În implementarea reală, aici am face un POST către backend
  };

  const handleAddReply = (reviewId: string, content: string) => {
    console.log(`Adding reply to review ${reviewId}: "${content}"`);
    // În implementarea reală, aici am face un POST către backend
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Skip Link pentru Screen Readers */}
      <a 
        href="#main-content" 
        className="skip-link"
        tabIndex={0}
      >
        {t('accessibility.skipToContent') || 'Skip to main content'}
      </a>
      
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow">
        {/* Hero Section modern */}
        <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          {/* Background Patterns */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white"></div>
            <div className="absolute top-1/4 left-1/4 w-48 h-48 rounded-full bg-white"></div>
            <div className="absolute bottom-12 right-1/3 w-64 h-64 rounded-full bg-white"></div>
          </div>
          
          <div className="container mx-auto px-4 py-20 relative z-10">
            <div className="flex flex-col items-center text-center">
              <SpeakableText element="h1" className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
                {t('hero.title')} <br className="hidden md:block" />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-yellow-200">
                  {t('hero.subtitle')}
                </span>
              </SpeakableText>
              
              <SpeakableText element="p" className="text-xl max-w-3xl mb-10 text-blue-100">
                {t('hero.description')}
              </SpeakableText>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link
                  to="/books"
                  className="px-8 py-4 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg"
                >
                  {t('hero.explore')}
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-4 bg-yellow-400 text-blue-900 font-bold rounded-xl hover:bg-yellow-300 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg"
                >
                  {t('hero.about')}
                </Link>
              </div>

              {/* Selector multilingv - mai vizibil */}
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 max-w-sm mx-auto">
                <div className="text-center mb-4">
                  <div className="text-blue-800 font-bold text-lg mb-2">
                    🌍 {t('hero.changeLanguage')}
                  </div>
                  <p className="text-gray-600 text-sm">
                    {t('hero.languageDescription')}
                  </p>
                </div>
                <MultilingualSelector />
              </div>
            </div>
          </div>
          
          {/* Wave divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 180">
              <path fill="#F9FAFB" fillOpacity="1" d="M0,96L60,106.7C120,117,240,139,360,138.7C480,139,600,117,720,101.3C840,85,960,75,1080,80C1200,85,1320,107,1380,117.3L1440,128L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"></path>
            </svg>
          </div>
        </section>

        {/* Resurse educaționale pentru elevi și profesori */}
        <section className="bg-white py-10 px-4">
          <div className="container mx-auto">
            <div className="rounded-2xl bg-gradient-to-r from-amber-50 via-white to-blue-50 border border-blue-100 shadow-lg p-6 md:p-10">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                <div className="text-center lg:text-left">
                  <p className="text-blue-700 font-bold uppercase tracking-wide text-sm mb-2">
                    Pentru clasele I–VIII
                  </p>
                  <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 leading-none mb-4">
                    1.500 de resurse educaționale
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 max-w-3xl">
                    Materiale pentru diverse discipline, cu peste 30% resurse interactive, pregătite pentru elevi și profesori.
                  </p>
                </div>
                <a
                  href="/centralizator_resurse_educationale_actualizat.xlsx"
                  download
                  className="flex-shrink-0 inline-flex items-center justify-center px-7 py-4 bg-blue-700 text-white font-bold rounded-xl hover:bg-blue-800 transition shadow-md text-lg"
                >
                  Descarcă lista cu resurse
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* Secțiunea de căutare */}
        <section className="py-8 px-4">
          <SearchBar onSearch={handleSearch} />
          
          {/* Buton căutare avansată */}
          <div className="text-center mt-2">
            <button 
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className="inline-flex items-center text-blue-600 hover:text-blue-800 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {showAdvancedSearch ? t('search.hideAdvanced') : t('search.advanced')}
            </button>
          </div>
          
          {/* Formularul de căutare avansată */}
          {showAdvancedSearch && (
            <div className="mt-6 animate-fadeIn">
              <AdvancedSearch 
                onApplyFilters={handleAdvancedSearch} 
                onClose={() => setShowAdvancedSearch(false)} 
              />
            </div>
          )}
          
          {searchQuery && (
            <div className="text-center mb-8 mt-8">
              {books.length > 0 ? (
                <h2 className="text-2xl font-semibold text-gray-800">
                  {t('search.resultsFor')} "{searchQuery}"
                </h2>
              ) : (
                <div className="py-6">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                    {t('search.noResults')} "{searchQuery}"
                  </h2>
                  <p className="text-gray-600">
                    {t('search.tryAnother')}
                  </p>
                </div>
              )}
            </div>
          )}
        </section>
        
        {/* Tabs pentru navigare între secțiuni */}
        {!searchQuery && (
          <div className="container mx-auto px-4 mb-8">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              <button
                className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'books'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('books')}
              >
                {t('tabs.booksCategories')}
              </button>
              <button
                className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'collections'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('collections')}
              >
                {t('tabs.specialCollections')}
              </button>
              <Link
                to="/recommendations"
                className={`px-4 py-3 font-medium text-sm whitespace-nowrap text-gray-600 hover:text-gray-800 transition-colors`}
              >
                {t('tabs.recommendations')}
              </Link>
              <button
                className={`px-4 py-3 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'external'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('external')}
              >
                {t('tabs.resources')}
              </button>
            </div>
          </div>
        )}
        
        {/* Secțiunea cărți sau categorii */}
        <section className="container mx-auto px-4 py-8">
          {searchQuery ? (
            // Rezultate căutare
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {books.map(book => (
                <div key={book.id} onClick={() => handleBookClick(book.id)}>
                  <BookCard 
                    id={book.id}
                    title={book.title}
                    author={book.author}
                    description={book.description}
                    coverImage={book.coverImage}
                    category={book.category}
                  />
                </div>
              ))}
            </div>
          ) : (
            // Conținut default
            <>
              {activeTab === 'books' && (
                <>
                  {/* Categorii populare */}
                  <div className="mb-16">
                    <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                      <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full inline-flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                        </svg>
                      </span>
                      {t('categories.popular')}
                    </h2>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                      {categories.map(category => (
                        <Link 
                          key={category.id}
                          to={`/books?category=${encodeURIComponent(category.name)}`}
                          className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1 text-center group"
                        >
                          <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">
                            {category.icon}
                          </div>
                          <h3 className="font-bold text-gray-800 mb-1 group-hover:text-blue-600 transition-colors">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-500">{category.count} {t('categories.resources')}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                  
                  {/* Cărți recente */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                      <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full inline-flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </span>
                      {t('books.recentlyAdded')}
                    </h2>
                    
                    <RecentlyAddedBooks limit={6} onBookClick={handleBookClick} />
                  </div>
                </>
              )}

              {activeTab === 'collections' && (
                <div className="animate-fadeIn">
                  <SpecialCollections 
                    collections={mockSpecialCollections}
                    onCollectionClick={handleCollectionClick}
                    onItemClick={handleCollectionItemClick}
                  />
                </div>
              )}

              {activeTab === 'external' && (
                <div className="animate-fadeIn">
                  <IntegratedResources 
                    resources={mockExternalResources}
                    onResourceClick={handleExternalResourceClick}
                    onSearch={handleSearch}
                  />
                </div>
              )}
            </>
          )}
        </section>

        {/* Footer info cu link către pagina de copyright */}
        <section className="bg-gray-100 py-8 mt-16">
          <div className="container mx-auto px-4 text-center">
            <div className="bg-white p-6 rounded-xl shadow-md max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {t('copyright.title')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('copyright.description')}
              </p>
              <Link 
                to="/copyright" 
                className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                {t('copyright.learnMore')}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      </main>
      
      {/* Book Reader/Preview */}
      {selectedBook && (
        <BookReaderPreview 
          book={selectedBook}
          onClose={handleCloseBookPreview}
        />
      )}
      
      {/* Componente de accesibilitate */}
      <AccessibilityButton />
      <AccessibilityPanel />
      
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

export default App;
